import { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { binderyApi } from "@/lib/api/bindery";
import { DAMAGE_TYPE_LABELS, REPAIR_TYPE_LABELS, formatMoney } from "./binderyFormatters";

interface VendorPerfRow {
  vendorId: string;
  vendorName: string;
  defaultTurnaroundDays: number;
  averageTurnaroundDays: number;
  batchesCompleted: number;
  totalSpent: number;
}

export default function BinderyHistoryPage() {
  const [repair, setRepair] = useState<{ repairType: string; count: number }[]>([]);
  const [damage, setDamage] = useState<{ damageType: string; count: number }[]>([]);
  const [vendors, setVendors] = useState<VendorPerfRow[]>([]);

  useEffect(() => {
    binderyApi.history
      .repairTypes()
      .then(setRepair)
      .catch(() => {});
    binderyApi.history
      .damageTypes()
      .then(setDamage)
      .catch(() => {});
    binderyApi.history
      .vendorPerformance()
      .then(setVendors)
      .catch(() => {});
  }, []);

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Bindery reports & history"
        description="Aggregate metrics to plan collection maintenance and review vendor performance."
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Bindery history" }]}
        showSearch={false}
        titleSize="md"
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4 text-slate-500" />
              Repair types — all time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FrequencyTable
              rows={repair.map((r) => ({
                label: REPAIR_TYPE_LABELS[r.repairType] ?? r.repairType,
                count: r.count,
              }))}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4 text-slate-500" />
              Damage types — all time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FrequencyTable
              rows={damage.map((d) => ({
                label: DAMAGE_TYPE_LABELS[d.damageType] ?? d.damageType,
                count: d.count,
              }))}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Vendor performance</CardTitle>
        </CardHeader>
        <CardContent>
          {vendors.length === 0 ? (
            <p className="text-sm text-slate-500">No vendor performance data yet.</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 text-left">Vendor</th>
                  <th className="py-2 text-right">Expected turnaround</th>
                  <th className="py-2 text-right">Avg actual</th>
                  <th className="py-2 text-right">Batches</th>
                  <th className="py-2 text-right">Spent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {vendors.map((v) => {
                  const delta = v.averageTurnaroundDays - v.defaultTurnaroundDays;
                  return (
                    <tr key={v.vendorId}>
                      <td className="py-2 font-medium text-slate-900">{v.vendorName}</td>
                      <td className="py-2 text-right tabular-nums">{v.defaultTurnaroundDays}d</td>
                      <td
                        className={`py-2 text-right tabular-nums ${
                          delta > 3 ? "text-red-600" : "text-slate-700"
                        }`}
                      >
                        {v.averageTurnaroundDays}d
                      </td>
                      <td className="py-2 text-right tabular-nums">{v.batchesCompleted}</td>
                      <td className="py-2 text-right tabular-nums">{formatMoney(v.totalSpent)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}

function FrequencyTable({ rows }: { rows: { label: string; count: number }[] }) {
  if (rows.length === 0) return <p className="text-sm text-slate-500">No data yet.</p>;
  const max = Math.max(...rows.map((r) => r.count));
  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <li key={r.label}>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-700">{r.label}</span>
            <span className="tabular-nums text-slate-500">{r.count}</span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: `${Math.round((r.count / max) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
